// Panel drag-to-expand — the PURE math for pulling a collapsed side panel open.
//
// Both side panels (the right dock, the left nav rail) can be pulled open from
// their collapsed state by grabbing the inner edge and dragging. This module is
// the pure core of that gesture: pointer-pull distance -> live width, and the
// release decision (settle open at the pulled width, or snap back to collapsed).
// The impure edge is the pointer wiring (PanelOpenHandle + the host); nothing
// here touches the DOM, time, or randomness — it is total, referentially
// transparent, and unit-reasonable in isolation.
//
// One gesture, one direction of "wider": the handle reports the pull ALREADY
// signed, so a positive `pullPx` always means "the author is pulling the panel
// WIDER" (the dock pulls leftward/inward, the rail rightward/outward; the handle
// folds that difference away with its growSign, exactly like PanelResizeHandle).

export type PanelDragConfig = {
    // The panel's collapsed base width in px (0 for the dock — its collapsed
    // body is zero-width; the icon-rail's collapsed width for the rail). The
    // live/opened width is this base plus the pull.
    readonly baseWidthPx: number;
    // The committed-open floor + ceiling (the panel's usable width clamp — the
    // same [min, max] the resize handle honours).
    readonly minWidthPx: number;
    readonly maxWidthPx: number;
    // How far PAST the base the author must pull before a release commits to
    // open; short of it the release snaps back to collapsed.
    readonly openThresholdPx: number;
};

// The width to render WHILE dragging: the base plus the pull, tracked 1:1 with
// the pointer and only capped at max (never floored at min — the preview must be
// free to be thin so the gesture reads as a physical pull, not a snap). A
// negative pull (dragging the wrong way) is clamped to the base.
export const panelDragWidth = (pullPx: number, cfg: PanelDragConfig): number =>
    Math.min(cfg.maxWidthPx, cfg.baseWidthPx + Math.max(0, pullPx));

// Has the pull crossed the commit line? (Drives the live "will open" affordance
// and the settle decision below.)
export const panelDragPastThreshold = (
    pullPx: number,
    cfg: PanelDragConfig,
): boolean => pullPx >= cfg.openThresholdPx;

// The release outcome — its own named sum, never a boolean.
export type PanelDragSettleOpen = {
    readonly type: "open";
    readonly widthPx: number;
};
export type PanelDragSettleCancel = { readonly type: "cancel" };
export type PanelDragSettle = PanelDragSettleOpen | PanelDragSettleCancel;

// On release: past the threshold settles OPEN at the pulled width (clamped to a
// usable [min, max] so a short commit still lands a usable panel); short of the
// threshold CANCELS back to collapsed.
export const panelDragSettle = (
    pullPx: number,
    cfg: PanelDragConfig,
): PanelDragSettle =>
    panelDragPastThreshold(pullPx, cfg)
        ? {
              type: "open",
              widthPx: Math.min(
                  cfg.maxWidthPx,
                  Math.max(cfg.minWidthPx, cfg.baseWidthPx + pullPx),
              ),
          }
        : { type: "cancel" };
